import * as THREE from 'three';
import { createRotationCenterTopologyFaceMarkerMaterial, createRotationCenterTopologyLineMarkerMaterial } from '../material';
import type { CameraTopologySnapshotSource } from './CameraTopologySnapshotSource';
import { ROTATION_CENTER_MARKER_COLOR } from './RotationCenterDebugVisual';

/**
 * 导入拓扑旋转中心的会话级调试标记。仅为最终元素创建一个显示对象，不创建 pick host，
 * 不修改源材质或共享几何。世界矩阵随旋转中心会话锁定，退出时归还全部独占资源。
 */
export class RotationCenterSnapshotMarker extends THREE.Group {
    /** 自有面/线标记；点使用已有中心十字，不重复生成 Sprite。 */
    private object: THREE.Mesh | THREE.Line | null = null;

    /** @param source 权威拓扑与实例世界空间。 @param kind 元素类型。 @param index 稳定索引。 */
    public constructor(source: CameraTopologySnapshotSource, kind: CameraTopologyElementKind, index: number) {
        super();
        this.name = '__rotation_center_snapshot_marker__';
        this.userData.skipSelectionSubtree = true;
        this.userData.isImportedModelOverlay = true;
        this.matrixAutoUpdate = false;
        this.matrix.copy(source.worldMatrix);
        this.matrixWorldNeedsUpdate = true;
        if (kind === 'feature-point') return;
        const positions = source.createMarkerPositions(kind, index);
        if (!positions.length) return;
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.object = kind === 'feature-face'
            ? new THREE.Mesh(geometry, createRotationCenterTopologyFaceMarkerMaterial(ROTATION_CENTER_MARKER_COLOR))
            : new THREE.Line(geometry, createRotationCenterTopologyLineMarkerMaterial(ROTATION_CENTER_MARKER_COLOR));
        this.object.renderOrder = 9001;
        this.object.frustumCulled = false;
        this.add(this.object);
    }

    /** @param _raycaster 射线。 @param _intersects 输出；调试层不参与拾取。 */
    public override raycast(_raycaster: THREE.Raycaster, _intersects: THREE.Intersection[]): void {}

    /** 幂等释放独占显示资源；原 Mesh 和拓扑数组从未被修改。 */
    public dispose(): void {
        this.removeFromParent();
        if (!this.object) return;
        this.object.geometry.dispose();
        const materials = Array.isArray(this.object.material) ? this.object.material : [this.object.material];
        for (const material of materials) material.dispose();
        this.object.removeFromParent();
        this.object = null;
    }
}
